import { useEffect, useState } from 'react';
import { useTranslation } from '@hotjar/translations';
import { Alert, Button, Stack, tokens } from '@hotjar/hotsauce';
import { useMfaSettings } from '@hotjar/webapp/domains/mfa/hooks/useMfaSettings';
import { MfaTrustedDevices } from '@hotjar/webapp/features/Mfa/MfaTrustedDevices';
import { DisableMfaButton } from '@hotjar/webapp/features/Mfa/DisableMfaButton/DisableMfaButton';
import { MfaDisableModal } from '@hotjar/webapp/features/Mfa/MfaDisable/MfaDisableModal';
import { MfaEnableModal } from '@hotjar/webapp/features/Mfa/MfaEnable/MfaEnableModal';
import { EnableMfaButton } from '@hotjar/webapp/features/Mfa/EnableMfaButton/EnableMfaButton';
import { useMfaModals } from '@hotjar/webapp/features/Mfa/useMfaModals';

import * as PROFILE from '@hotjar/webapp/constants/routes/profile';
import { useCurrentUser } from '@hotjar/user';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { SectionHeader, StyledField, Wrapper } from '../styles';
import { SuccessMessageWithIcon } from '../../../components';

const StyledSuccessMessageWithIcon = styled(SuccessMessageWithIcon)`
  margin-top: ${tokens.spacer.x1pt5};
  margin-bottom: ${tokens.spacer.x1pt5};
`;

const UnavailableMessage = styled(Alert)`
  margin-bottom: ${tokens.spacer.x1pt5};
`;

export const SecurityDetail = (): JSX.Element => {
  const navigate = useNavigate();
  const { t } = useTranslation('profile');
  const { data: user, refetch: refetchCurrentUser } = useCurrentUser();
  const {
    data: mfaSettings,
    refetch: refetchMfaSettings,
    isError: isMfaError,
  } = useMfaSettings(user?.id);
  const [showPopover, setPopoverState] = useState(false);
  const { setEnableMfa, setDisableMfa } = useMfaModals();

  const onOpenDisableMFAModal = () => setDisableMfa(true);
  const onOpenEnableMFAModal = () => setEnableMfa(true);

  const onMfaDisabled = () => {
    refetchMfaSettings();
  };

  const onMfaEnabled = () => {
    refetchMfaSettings();
  };

  const onEmailVerified = () => {
    refetchCurrentUser();
  };

  const isHotjarian = user?.email?.endsWith('@hotjar.com') ?? false;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (mfaSettings?.enabled === false && params.get('ref') === 'enforce_mfa') {
      setPopoverState(true);
    }
  }, [mfaSettings]);

  return (
    <>
      {user !== undefined && user.email !== null && (
        <>
          <MfaDisableModal userId={user?.id} onDisabled={onMfaDisabled} />

          <MfaEnableModal
            userEmail={user.email}
            userId={user?.id}
            onClickChangeEmail={() => navigate(PROFILE.EMAIL.path)}
            isEmailVerified={user.isEmailVerified}
            onEmailVerified={onEmailVerified}
            onEnabled={onMfaEnabled}
          />
        </>
      )}

      <Wrapper>
        <SectionHeader textType="header5" as="h2">
          {t('security-detail.title')}
        </SectionHeader>
        <Stack preset="form">
          <StyledField labelText={t('security-detail.password')} inputId="change-password">
            <Button
              id="change-password-button"
              onClick={() => navigate(PROFILE.PASSWORD.path)}
              type="button"
            >
              {t('security-detail.change-password')}
            </Button>
          </StyledField>

          <StyledField
            labelText={t('security-detail.two-factor')}
            helperText={t('security-detail.two-factor-help')}
            inputId="two-factor"
          >
            {isMfaError && (
              <UnavailableMessage kind="warning">
                {t('security-detail.two-factor-unavailable')}
              </UnavailableMessage>
            )}

            {mfaSettings?.enabled === true && (
              <StyledSuccessMessageWithIcon message={t('security-detail.two-factor-is-enabled')} />
            )}

            <MfaTrustedDevices />

            {!isMfaError && (
              <>
                <DisableMfaButton
                  isVisible={mfaSettings?.enabled === true}
                  isMfaEnforced={mfaSettings?.hasEnforcedMfa === true}
                  onClick={onOpenDisableMFAModal}
                />

                <EnableMfaButton
                  isVisible={mfaSettings?.enabled === false}
                  isPopoverVisible={showPopover}
                  onClick={onOpenEnableMFAModal}
                />
              </>
            )}
          </StyledField>

          {isHotjarian && (
            <StyledField
              labelText={t('security-detail.sign-in-activity')}
              inputId="sign-in-activity"
            >
              <Button href={PROFILE.SIGN_IN_ACTIVITY.path} as="a">
                {t('security-detail.view-sign-in-activity')}
              </Button>
            </StyledField>
          )}
        </Stack>
      </Wrapper>
    </>
  );
};
